import { useEffect, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import type { Textfield } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import {
    CONTACTS_PER_COMPANY_DEFAULT_PLACEHOLDER,
    CONTACTS_PER_COMPANY_LIMIT_INPUT_DEBOUNCE_MS,
    CONTACTS_PER_COMPANY_LIMIT_TRANSLATIONS,
} from "./constants";
import { StyledContactsPerCompanyLimitRow, StyledContactsPerCompanyLimitTextField } from "./styles";

type ContactsPerCompanyLimitInputProps = {
    value?: number;
    defaultPlaceholder?: number;
    maxValue?: number;
    maxValueErrorMessage?: string;
    onChange: (value?: number) => void;
    dataAutomation?: string;
};

export const ContactsPerCompanyLimitInput = ({
    value,
    defaultPlaceholder = CONTACTS_PER_COMPANY_DEFAULT_PLACEHOLDER,
    maxValue,
    maxValueErrorMessage,
    onChange,
    dataAutomation = "contacts-per-company-limit-input",
}: ContactsPerCompanyLimitInputProps) => {
    const { translate } = useTranslate();
    const invalidValueErrorMessage = translate(CONTACTS_PER_COMPANY_LIMIT_TRANSLATIONS.textInputErrorMessage);
    const hasInitialMaxError = maxValue !== undefined && value !== undefined && value > maxValue;
    const [inputError, setInputError] = useState(hasInitialMaxError);
    const [errorMessage, setErrorMessage] = useState(
        hasInitialMaxError ? (maxValueErrorMessage ?? invalidValueErrorMessage) : invalidValueErrorMessage,
    );
    const inputRef = useRef<Textfield>(null);

    useEffect(() => {
        inputRef.current?.setValue(value === undefined ? "" : value.toString());
    }, [value]);

    const handleSelection = (textValue: string) => {
        if (!textValue.trim()) {
            onChange(undefined);
            setInputError(false);
            return;
        }

        const parsedValue = parseInt(textValue, 10);

        if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
            onChange(undefined);
            setInputError(true);
            setErrorMessage(invalidValueErrorMessage);
            return;
        }

        if (maxValue !== undefined && parsedValue > maxValue) {
            onChange(undefined);
            setInputError(true);
            setErrorMessage(maxValueErrorMessage ?? invalidValueErrorMessage);
            return;
        }

        onChange(parsedValue);
        setInputError(false);
    };

    return (
        <StyledContactsPerCompanyLimitRow
            $inputError={inputError}
            data-automation={dataAutomation}
        >
            <Text fontVariant="input2">{translate(CONTACTS_PER_COMPANY_LIMIT_TRANSLATIONS.prefix)}</Text>
            <StyledContactsPerCompanyLimitTextField
                ref={inputRef}
                onChange={handleSelection}
                placeholder={defaultPlaceholder.toString()}
                error={inputError}
                errorMessage={errorMessage}
                maxLength={3}
                debounce={CONTACTS_PER_COMPANY_LIMIT_INPUT_DEBOUNCE_MS}
                className={`max-results-input ${inputError ? "error" : ""}`}
            />
            <Text fontVariant="input2">{translate(CONTACTS_PER_COMPANY_LIMIT_TRANSLATIONS.suffix)}</Text>
        </StyledContactsPerCompanyLimitRow>
    );
};
